import { format } from 'util';
import { logToSentry } from '../sentry';
import { Level, LevelPriority } from '../constants';
import { getLoggingLevel } from './loggingLevel';
import isInDev from '../isInDev';
import isOnServer from '../isOnServer';
import redactor from '../redaction';
/**
 * Should this logging statement produce output?
 * @private
 */
export const shouldLog = (level, localLevel) => LevelPriority[level] >= LevelPriority[localLevel !== null && localLevel !== void 0 ? localLevel : getLoggingLevel()];
/**
 * This will log to the console based on the supplied log level, and the global log level.
 * Note that the global log level is initialised according to the NODE_ENV and existence of
 * proces.browser. Once initialised, it can be changed by using setLoggingLevel.
 *
 * - We will always log to the browser console as well as the server console in dev mode (and this is not redacted).
 * - We will log nothing to the browser console in non-dev mode.
 * - We will log some stuff to the server console in non-dev mode (this is redacted).
 * @private
 * @param level - log or error.
 * @param messages - varargs, same as console.log or console.error.
 */
export const logInner = (args) => {
    const { level, messages, localLevel, loggerName, tags = {} } = args;
    if (shouldLog(level, localLevel)) {
        const dateString = new Date().toISOString();
        if (!isOnServer()) {
            // In client, we construct a readable message and use the proper console methods to log it.
            const consoleArgs = [dateString];
            if (loggerName)
                consoleArgs.push(`[${loggerName}]`);
            consoleArgs.push(...messages);
            // eslint-disable-next-line no-console
            console[level](...consoleArgs);
        }
        else {
            // Always log on the server.
            // This is designed to match bole's output, which is already handled by elk
            // and pretty printed in local dev via garnish by most apps
            const effectiveLevel = level === Level.error && !isInDev() ? Level.error : Level.log;
            // eslint-disable-next-line no-console
            console[effectiveLevel](JSON.stringify({
                time: dateString,
                level,
                message: format(
                // Always redact logs when not in dev.
                ...(isInDev() ? messages : redactor.apply(messages))),
                name: loggerName,
                url: tags.endpoint,
                tags: Object.assign({ loggerName }, tags),
            }));
        }
    }
    // Notify Sentry of anything that's logged at Error level in non-dev mode
    if (!isInDev() && level === Level.error) {
        logToSentry(messages);
    }
};
